import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

// Flag components as SVGs for better cross-platform compatibility
const GBFlag = ({ className }) => (
  <svg className={className} viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg">
    <path fill="#012169" d="M0 0h640v480H0z"/>
    <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
    <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
    <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
  </svg>
);

const NLFlag = ({ className }) => (
  <svg className={className} viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg">
    <path fill="#21468B" d="M0 0h640v480H0z"/>
    <path fill="#FFF" d="M0 0h640v320H0z"/>
    <path fill="#AE1C28" d="M0 0h640v160H0z"/>
  </svg>
);

const languages = [
  { code: 'en', name: 'English', Flag: GBFlag },
  { code: 'nl', name: 'Nederlands', Flag: NLFlag },
];

export default function LanguageSwitcher({ mobile = false }) {
  const { i18n, t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('craftech-language', langCode);
    setIsOpen(false);
  };

  if (mobile) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
          <GlobeAltIcon className="w-4 h-4" />
          <span>{t('language.selectLanguage')}</span>
        </div>
        <div className="flex gap-3">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`flex items-center justify-center w-12 h-9 rounded-lg overflow-hidden transition-all duration-300 ${
                currentLanguage.code === lang.code
                  ? 'ring-2 ring-primary ring-offset-2 ring-offset-gray-900 scale-110'
                  : 'opacity-60 hover:opacity-100 hover:scale-105'
              }`}
              title={lang.name}
            >
              <lang.Flag className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/10 transition-all duration-300"
        title={currentLanguage.name}
      >
        <div className="w-7 h-5 rounded overflow-hidden shadow-sm">
          <currentLanguage.Flag className="w-full h-full" />
        </div>
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      <div
        className={`absolute top-full right-0 mt-2 transition-all duration-300 ${
          isOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-2'
        }`}
      >
        {/* Arrow */}
        <div className="absolute -top-2 right-4 w-4 h-4 bg-gray-800 rotate-45 border-l border-t border-white/10"></div>

        <div className="relative bg-gray-800/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl shadow-black/20 overflow-hidden">
          {/* Decorative gradient line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>

          <div className="p-2 flex gap-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-10 h-7 rounded overflow-hidden transition-all duration-300 ${
                  currentLanguage.code === lang.code
                    ? 'ring-2 ring-primary scale-110'
                    : 'opacity-60 hover:opacity-100 hover:scale-105'
                }`}
                title={lang.name}
              >
                <lang.Flag className="w-full h-full" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
